import { CalciteLabel } from "@esri/calcite-components-react";
import { Button } from "components/Button";
import { Card, CardHeader } from "components/Card";
import { Input } from "components/Input";
import { UserContext } from "context/UserContext";
import React, { useContext } from "react";
import { useLoaderData, useNavigate } from "react-router-dom";
import { AliRecord, disabilityOptions } from "services/ALIRecord.d";
import RecordService from "services/RecordService";
import {
  formatHouseNumber,
  formatPhoneNumber,
  formatZipCode,
} from "utils/helpers";

const recordService = new RecordService("ali");

// Loader function
export const loadRecord = async ({ params }): Promise<AliRecord> => {
  try {
    const res = await recordService.getRecords(
      {
        criterias: [{ field: "CPN", type: "=", value: params.cpn }],
      },
      "searchByCriteria",
    );

    if (res.status === 401) {
      throw new Error("Unable to fetch record");
    }

    if (!res.data?.message?.list?.length) {
      throw new Error("Record not found");
    }

    return res.data.message.list[0];
  } catch (error: Error | any) {
    throw new Error(error.message || "Failed to load record");
  }
};

// Sub-components
const DisabilityCodes: React.FC<{ record: AliRecord }> = ({ record }) => (
  <div>
    <Input
      name="privateCitizen"
      label="Private Citizen"
      value={record.privateCit ? "Yes" : "No"}
      readonly
      scale="s"
      labelSize="s"
    />

    <CalciteLabel style={{ marginBottom: "4px", marginTop: "10px" }} scale="s">
      Disability Codes*:
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          marginTop: "8px",
        }}
      >
        {disabilityOptions.map(({ value, label }) => (
          <CalciteLabel
            key={value}
            layout="inline"
            style={{ marginRight: "12px" }}
            scale="s"
          >
            <input
              type="checkbox"
              checked={record?.dCode?.includes(value)}
              style={{ marginRight: "6px" }}
              disabled
            />
            {label}
          </CalciteLabel>
        ))}
      </div>
    </CalciteLabel>
  </div>
);

const LeftColumn: React.FC<{ record: AliRecord; user: any }> = ({
  record,
  user,
}) => (
  <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
    <Input
      name="telephoneNumber"
      label="Telephone Number"
      value={formatPhoneNumber(record.CPN || "")}
      readonly
      scale="s"
      labelSize="s"
    />
    <Input
      name="customerName"
      label="Customer Name"
      value={record.NAM || ""}
      readonly
      scale="s"
      labelSize="s"
    />
    <Input
      name="state"
      label="State/Province"
      value={record.STA || ""}
      readonly
      scale="s"
      labelSize="s"
    />
    <Input
      name="mcn"
      label="MSAG Community Name"
      value={record.MCN || ""}
      readonly
      scale="s"
      labelSize="s"
    />
    <Input
      name="streetName"
      label="Street Name"
      value={record.STN || ""}
      readonly
      scale="s"
      labelSize="s"
    />
    <Input
      name="streetSuffix"
      label="Street Suffix"
      value={record.STS || ""}
      readonly
      scale="s"
      labelSize="s"
    />
    <Input
      name="prefixDirectional"
      label="Prefix Directional"
      value={record.PRD || ""}
      readonly
      scale="s"
      labelSize="s"
    />
    <Input
      name="postDirectional"
      label="Post Directional"
      value={record.POD || ""}
      readonly
      scale="s"
      labelSize="s"
    />
    <Input
      name="houseNumber"
      label="House Number"
      value={formatHouseNumber(record, record.HNO) || ""}
      readonly
      scale="s"
      labelSize="s"
    />
    <Input
      name="houseNumberSuffix"
      label="House Number Suffix"
      value={record.HNS || ""}
      readonly
      scale="s"
      labelSize="s"
    />
    <Input
      name="location"
      label="Location"
      value={record.LOC || ""}
      readonly
      scale="s"
      labelSize="s"
    />
    {user?.functions?.includes("foresite:ali:addlData:write") && (
      <DisabilityCodes record={record} />
    )}
  </div>
);

const RightColumn: React.FC<{ record: AliRecord }> = ({ record }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
    <Input
      name="foc"
      label="Function of Change"
      value={record.FOC || ""}
      readonly
      scale="s"
      labelSize="s"
    />
    <Input
      name="nenaId"
      label="NENA ID"
      value={record.CPF || ""}
      readonly
      scale="s"
      labelSize="s"
    />
    <Input
      name="classOfService"
      label="Class of Service"
      value={record.CLS || ""}
      readonly
      scale="s"
      labelSize="s"
    />
    <Input
      name="typeOfService"
      label="Type of Service"
      value={record.TYS || ""}
      readonly
      scale="s"
      labelSize="s"
    />
    <Input
      name="generalUse"
      label="General Use"
      value={record.GeneralUse || ""}
      readonly
      scale="s"
      labelSize="s"
    />
    <Input
      name="comments"
      label="Comments"
      value={record.Comments || ""}
      readonly
      scale="s"
      labelSize="s"
    />
    <Input
      name="zipCode"
      label="Zip Code"
      value={formatZipCode(record.ZIP) || ""}
      readonly
      scale="s"
      labelSize="s"
    />
    <Input
      name="zipExtension"
      label="Zip Extension"
      value={record.lecZipExtension || ""}
      readonly
      scale="s"
      labelSize="s"
    />
    <Input
      name="areaCode"
      label="Area Code of Main Number"
      value={record.MainNPA || ""}
      readonly
      scale="s"
      labelSize="s"
    />
    <Input
      name="mainNumber"
      label="Main Number"
      value={record.MainNumber || ""}
      readonly
      scale="s"
      labelSize="s"
    />
  </div>
);

const AdditionalInfoSection: React.FC<{
  record: AliRecord;
  showAdditionalInfo: boolean;
  setShowAdditionalInfo: (
    value: boolean | ((prev: boolean) => boolean),
  ) => void;
}> = ({ record, showAdditionalInfo, setShowAdditionalInfo }) => {
  const handleToggleAdditionalInfo = () => {
    setShowAdditionalInfo((prev) => {
      const next = !prev;
      if (!prev) {
        setTimeout(() => {
          const exchangeInput = document.querySelector(
            'calcite-input[name="Exchange"]',
          ) as any | null;
          exchangeInput?.setFocus();
        }, 100);
      }
      return next;
    });
  };

  return (
    <>
      <hr style={{ margin: "20px 0" }} />
      <div style={{ padding: "0 20px" }}>
        <div style={{ marginBottom: "20px", textAlign: "center" }}>
          {showAdditionalInfo && (
            <h3 style={{ marginBottom: "10px" }}>Additional Information</h3>
          )}
        </div>

        <div
          style={{
            overflow: "hidden",
            transition:
              "max-height 0.5s cubic-bezier(0.4,0,0.2,1), opacity 0.5s",
            maxHeight: showAdditionalInfo ? 1000 : 0,
            opacity: showAdditionalInfo ? 1 : 0,
          }}
        >
          {showAdditionalInfo && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "100px",
              }}
            >
              <div
                style={{ display: "flex", flexDirection: "column", gap: "8px" }}
              >
                <Input
                  name="exchange"
                  label="Exchange"
                  value={record.Exchange || ""}
                  readonly
                  scale="s"
                  labelSize="s"
                />
                <Input
                  name="orderNumber"
                  label="Order Number"
                  value={record.OrderNumber || ""}
                  readonly
                  scale="s"
                  labelSize="s"
                />
                <Input
                  name="latitude"
                  label="Latitude"
                  value={record.LAT || ""}
                  readonly
                  scale="s"
                  labelSize="s"
                />
                <Input
                  name="longitude"
                  label="Longitude"
                  value={record.LON || ""}
                  readonly
                  scale="s"
                  labelSize="s"
                />
              </div>
              <div
                style={{ display: "flex", flexDirection: "column", gap: "8px" }}
              >
                <Input
                  name="elevation"
                  label="Elevation"
                  value={record.ELV || ""}
                  readonly
                  scale="s"
                  labelSize="s"
                />
                <Input
                  name="nenaReserved"
                  label="NENA Reserved"
                  value={record.NENAReserved || ""}
                  readonly
                  scale="s"
                  labelSize="s"
                />
                <Input
                  name="dataProviderId"
                  label="Data Provider ID"
                  value={record.DataProviderID || ""}
                  readonly
                  scale="s"
                  labelSize="s"
                />
                <Input
                  name="reserved2"
                  label="Reserved 2"
                  value={record.Reserved2 || ""}
                  readonly
                  scale="s"
                  labelSize="s"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: showAdditionalInfo ? "20px" : "0",
        }}
      >
        <Button
          variant="outline"
          kind="brand"
          onClick={handleToggleAdditionalInfo}
        >
          {showAdditionalInfo ? "Hide" : "Show"} Additional Information
        </Button>
      </div>
    </>
  );
};

// Main component
export default function ALIRead() {
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const [showAdditionalInfo, setShowAdditionalInfo] = React.useState(false);
  const record = useLoaderData() as AliRecord;

  if (!record) {
    return <div>Loading...</div>;
  }

  return (
    <div>
      <CardHeader>ALI Record Details</CardHeader>
      <Card style={{ width: "100%", marginBottom: "20px" }}>
        <div style={{ padding: "20px", paddingBottom: "40px" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "100px",
            }}
          >
            <LeftColumn record={record} user={user} />
            <RightColumn record={record} />
          </div>

          <AdditionalInfoSection
            record={record}
            showAdditionalInfo={showAdditionalInfo}
            setShowAdditionalInfo={setShowAdditionalInfo}
          />

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: "20px",
              gap: "8px",
            }}
          >
            <Button
              variant="outline"
              kind="brand"
              label="Close"
              onClick={() => navigate(-1)}
            >
              Close
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
